<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useTranslation } from '@/utils/locales/messages';
import { sgEntityLookupApi } from '../../../api/sgEntityLookupApi';
import type { SgAddress, SgAddressType } from '../utils/sgLocalAddress';
import { formatSgAddressLine } from '../utils/sgLocalAddress';

const props = defineProps<{
  modelValue: SgAddress;
  title?: string;
  readonly?: boolean;
  required?: boolean;
  /** Registered Address 등 SG 로컬 주소만 허용하는 필드 */
  localOnly?: boolean;
  /** "층/호수 없음" 체크박스 노출 (Contact address 등) */
  showLevelUnitToggle?: boolean;
  /**
   * 엄격 검증: 로컬 주소의 블록/거리/건물명까지 필수 + Level/Unit 은 최대 5자.
   *   (다른 호출부 영향 없이 opt-in — Incorporation E-Filing 회사정보 등에서만 사용)
   */
  strict?: boolean;
}>();

const emit = defineEmits<{ 'update:modelValue': [value: SgAddress] }>();

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

/** 필수 룰 (빈 값 금지). */
const requiredRule = (v: string) =>
  !!String(v ?? '').trim() || t('setupEntity.sgIncorporation.validation.required') || 'Required';
/**
 * Level/Unit 룰. "층/호수 없음" 미체크(haveLevelUnit !== false)면 필수 + 최대 5자.
 *   체크 시엔 검증 안 함(값 없음이 정상).
 */
const levelUnitRule = (v: string) => {
  if (props.modelValue.haveLevelUnit === false) return true;
  const s = String(v ?? '').trim();
  if (!s) return t('setupEntity.sgIncorporation.validation.required') || 'Required';
  if (s.length > 5) {
    return t('setupEntity.sgIncorporation.validation.levelUnitMax') || 'Level/Unit must be 5 characters or fewer';
  }
  return true;
};

const isSearchingPostalCode = ref(false);
const isPostalCodeSearched = ref(false);
const postalCodeError = ref<string | null>(null);

const isLocal = computed(
  () => props.localOnly || props.modelValue.addressTypeCd !== 'B07002',
);
const combinedAddress = computed(() => formatSgAddressLine(props.modelValue));

function patch(partial: Partial<SgAddress>) {
  emit('update:modelValue', { ...props.modelValue, ...partial });
}

function onNoLevelUnit(v: boolean | null) {
  patch(v ? { haveLevelUnit: false, level: '', unit: '' } : { haveLevelUnit: true });
}

function updateAddressType(value: SgAddressType) {
  if (props.localOnly) return;
  patch({ addressTypeCd: value });
}

watch(
  () => [props.localOnly, props.modelValue.addressTypeCd] as const,
  ([localOnly, addressTypeCd]) => {
    if (localOnly && addressTypeCd === 'B07002') {
      patch({ addressTypeCd: 'B07001' });
    }
  },
  { immediate: true },
);

async function searchPostalCode() {
  if (props.readonly || !isLocal.value) return;
  const postalCode = props.modelValue.postalCode?.trim();
  if (!postalCode) return;

  isSearchingPostalCode.value = true;
  postalCodeError.value = null;
  try {
    const response = await sgEntityLookupApi.searchPostalCode(postalCode);
    const result = (response as any).data?.data || (response as any).data || response;
    if (result) {
      patch({
        streetName: result.streetName ?? result.street_name ?? '',
        buildingName: result.buildingName ?? result.building_name ?? '',
        blockNo: result.blockHouseNo ?? result.block_house_no ?? result.blockNo ?? '',
      });
      isPostalCodeSearched.value = true;
    } else {
      postalCodeError.value = t('entity.Postal code not found') || 'Postal code not found';
    }
  } catch {
    postalCodeError.value = t('entity.Failed to search postal code') || 'Failed to search postal code';
  } finally {
    isSearchingPostalCode.value = false;
  }
}

watch(
  () => props.modelValue.postalCode,
  () => {
    postalCodeError.value = null;
    isPostalCodeSearched.value = false;
  },
);
</script>

<template>
  <div class="sg-address-block mb-4">
    <h5 v-if="title" class="text-subtitle-2 font-weight-medium mb-3">{{ title }}</h5>

    <v-radio-group
      v-if="!localOnly"
      :model-value="modelValue.addressTypeCd"
      :readonly="readonly"
      class="mb-3"
      inline
      hide-details
      @update:model-value="updateAddressType"
    >
      <v-radio
        :label="t('contact.Local Address') || 'Local Address'"
        value="B07001"
      />
      <v-radio
        :label="t('contact.Foreign Address') || 'Foreign Address'"
        value="B07002"
      />
    </v-radio-group>

    <v-alert
      v-if="isLocal && postalCodeError"
      type="error"
      density="compact"
      class="mb-3"
      closable
      @click:close="postalCodeError = null"
    >
      {{ postalCodeError }}
    </v-alert>

    <template v-if="isLocal">
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            :model-value="modelValue.postalCode"
            :label="t('entity.Postal Code') || 'Postal Code'"
            :readonly="readonly"
            variant="outlined"
            density="comfortable"
            :hide-details="required || strict ? false : true"
            :loading="isSearchingPostalCode"
            :rules="required || strict ? [requiredRule] : undefined"
            @update:model-value="patch({ postalCode: $event })"
          >
            <template #append-inner>
              <v-icon
                v-if="!readonly"
                size="small"
                class="cursor-pointer"
                @click.stop="searchPostalCode"
              >
                mdi-magnify
              </v-icon>
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="6" />

        <v-col cols="12" md="4">
          <v-text-field
            :model-value="modelValue.blockNo"
            :label="t('entity.Block/House No') || 'Block/House No'"
            :readonly="readonly || isPostalCodeSearched"
            variant="filled"
            density="comfortable"
            hide-details
            @update:model-value="patch({ blockNo: $event })"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            :model-value="modelValue.streetName"
            :label="t('entity.Street Name') || 'Street Name'"
            :readonly="readonly || isPostalCodeSearched"
            variant="filled"
            density="comfortable"
            hide-details
            @update:model-value="patch({ streetName: $event })"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            :model-value="modelValue.buildingName"
            :label="t('entity.Building/Estate Name') || 'Building/Estate Name'"
            :readonly="readonly || isPostalCodeSearched"
            variant="filled"
            density="comfortable"
            hide-details
            @update:model-value="patch({ buildingName: $event })"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            :model-value="modelValue.level"
            :label="t('entity.Level') || 'Level'"
            :readonly="readonly"
            :disabled="showLevelUnitToggle && modelValue.haveLevelUnit === false"
            variant="outlined"
            density="comfortable"
            :hide-details="strict ? false : true"
            :rules="strict ? [levelUnitRule] : undefined"
            @update:model-value="patch({ level: $event })"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            :model-value="modelValue.unit"
            :label="t('entity.Unit') || 'Unit'"
            :readonly="readonly"
            :disabled="showLevelUnitToggle && modelValue.haveLevelUnit === false"
            variant="outlined"
            density="comfortable"
            :hide-details="strict ? false : true"
            :rules="strict ? [levelUnitRule] : undefined"
            @update:model-value="patch({ unit: $event })"
          />
        </v-col>
        <v-col v-if="showLevelUnitToggle" cols="12">
          <v-checkbox
            :model-value="modelValue.haveLevelUnit === false"
            :label="t('setupEntity.sgIncorporation.contact.noLevelUnit')"
            :readonly="readonly"
            hide-details
            density="comfortable"
            @update:model-value="onNoLevelUnit"
          />
        </v-col>
      </v-row>
    </template>

    <template v-else>
      <v-row dense>
        <v-col cols="12">
          <v-text-field
            :model-value="modelValue.foreignAddressLine1"
            :label="t('contact.Foreign Address Line 1') || 'Foreign Address Line 1'"
            :rules="required ? [(v: string) => !!v?.trim() || t('setupEntity.sgIncorporation.validation.required')] : undefined"
            :hide-details="required ? false : true"
            :readonly="readonly"
            variant="outlined"
            density="comfortable"
            @update:model-value="patch({ foreignAddressLine1: $event })"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            :model-value="modelValue.foreignAddressLine2"
            :label="t('contact.Foreign Address Line 2') || 'Foreign Address Line 2'"
            :readonly="readonly"
            variant="outlined"
            density="comfortable"
            hide-details
            @update:model-value="patch({ foreignAddressLine2: $event })"
          />
        </v-col>
      </v-row>
    </template>

    <v-textarea
      :model-value="combinedAddress"
      :label="t('setupEntity.sgIncorporation.address.combinedPreview')"
      variant="filled"
      density="comfortable"
      rows="2"
      auto-grow
      readonly
      class="mt-3 combined-address-preview"
      :placeholder="t('setupEntity.sgIncorporation.address.combinedPreviewEmpty')"
    />
  </div>
</template>

<style scoped>
.sg-address-block {
  padding: 12px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 10px;
}
.cursor-pointer {
  cursor: pointer;
}
.combined-address-preview :deep(.v-field__input) {
  color: rgba(var(--v-theme-on-surface), 0.87);
}
</style>
